/**
 * La ficha de una persona (`/activa/emails/personas/:id`): todo lo suyo en las campañas de mail del comercio. Se abre
 * por el id de una de sus filas (nunca con el mail en la URL); una de otro comercio es un 404 como una que no existe.
 *
 * - Sus datos, con el teléfono entero (en las tablas va enmascarado: abrir la ficha queda en el log del servidor);
 * - si está en «No enviar» de Snov y en las bajas de mail de Activa;
 * - cada campaña que recibió, con lo que hizo (aperturas y clics sólo donde se miden);
 * - lo que le pasó, del más nuevo al más viejo, y lo que contestó.
 */

import { Link, useParams } from "react-router-dom";
import { ArrowLeft, UserMinus } from "lucide-react";
import { emailsApi } from "../api";
import type { FichaPersona, TipoEvento } from "../types";
import { usePedido } from "../hooks/usePedido";
import { useEstadoEmails } from "../contexto";
import { Tarjeta } from "../../wa-oficial/components/Tarjeta";
import { Esqueleto, ErrorTarjeta, Vacio } from "../../wa-oficial/components/estadisticas/comun";
import { num } from "../../campana-nueva/utils/format";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { ChipEstadoPersona } from "../components/Chips";
import { ErrorRefresco } from "../components/ErrorRefresco";
import { R_EMAILS, asuntoLegible, diaMesHora } from "../logic/emails";

const TEXTO_EVENTO: Record<TipoEvento, { texto: string; cls: string }> = {
  enviado: { texto: "Se le mandó", cls: "bg-slate-300" },
  rebote: { texto: "Rebotó", cls: "bg-amber-500" },
  apertura: { texto: "Abrió", cls: "bg-sky-500" },
  clic: { texto: "Hizo clic", cls: "bg-emerald-500" },
  respuesta: { texto: "Contestó", cls: "bg-violet-500" },
  autorespuesta: { texto: "Respuesta automática", cls: "bg-slate-400" },
  baja: { texto: "Se dio de baja con el link", cls: "bg-red-500" },
};

const Marca = ({ children, cls, title }: { children: string; cls: string; title?: string }) => (
  <span title={title} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${cls}`}>{children}</span>
);

export default function PersonaEmailPage() {
  const { id = "" } = useParams();
  const { version } = useEstadoEmails();
  const equipo = useEsEquipoDeenex();
  const r = usePedido<FichaPersona>(() => emailsApi.persona(id), id, { refresco: version });
  const p = r.dato;
  const volver = (
    <Link to={`${R_EMAILS}/personas`} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900"><ArrowLeft size={15} /> Personas</Link>
  );

  if (!p) {
    return (
      <div className="space-y-4">
        {volver}
        {r.error
          ? <ErrorTarjeta texto={r.code === "NOT_FOUND" ? "No encontramos a esa persona en las campañas de este comercio." : `No pudimos traer la ficha. ${r.error}`} onReintentar={r.code === "NOT_FOUND" ? undefined : r.recargar} />
          : <Esqueleto alto="h-64" etiqueta="Cargando la ficha" />}
      </div>
    );
  }

  const datos = [p.telefono && { k: "Teléfono", v: p.telefono }, p.empresa && { k: "Empresa", v: p.empresa }, p.ubicacion && { k: "Ubicación", v: p.ubicacion }].filter(Boolean) as { k: string; v: string }[];
  const sigueRecibiendo = p.estado === "pidio_baja" && !p.enNoEnviar;

  return (
    <div className="space-y-5">
      {volver}
      <header className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight break-words">{p.nombre || "Sin nombre"}</h2>
          <ChipEstadoPersona estado={p.estado} />
          {p.enNoEnviar && <Marca cls="bg-slate-800 text-white" title="Está en la lista de «No enviar» de Snov: ninguna campaña le escribe">En «No enviar»</Marca>}
          {p.enBajasActiva && <Marca cls="bg-red-50 text-red-700" title="Tiene una baja de mail en Activa: las campañas por QR no le mandan mails">Baja de mail en Activa</Marca>}
        </div>
        <p className="text-sm text-slate-600 break-all">{p.email}</p>
        {datos.length > 0 && (
          <dl className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
            {datos.map((d) => (
              <div key={d.k} className="flex gap-1.5">
                <dt className="text-slate-500">{d.k}:</dt>
                <dd className="text-slate-800 break-words">{d.v}</dd>
              </div>
            ))}
          </dl>
        )}
        {r.error && <ErrorRefresco error={r.error} onReintentar={r.recargar} />}
      </header>

      {sigueRecibiendo && (
        <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900" role="status">
          <UserMinus size={18} className="mt-0.5 flex-shrink-0 text-red-600" />
          <p>
            Pidió la baja contestando y todavía no está en «No enviar»: la próxima campaña le vuelve a escribir.{" "}
            {equipo
              ? <Link to={R_EMAILS} className="font-semibold underline">Pasarla desde el resumen</Link>
              : "Pedile al equipo de Deenex que la pase a «No enviar»."}
          </p>
        </div>
      )}

      <Tarjeta titulo="Campañas que recibió">
        {!p.campanas.length ? <Vacio>Sin campañas.</Vacio> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="py-2 pr-3">Campaña</th>
                  <th className="py-2 px-2">Cómo le fue</th>
                  <th className="py-2 px-2 text-right">Mails</th>
                  <th className="py-2 px-2 text-right">Aperturas</th>
                  <th className="py-2 px-2 text-right">Clics</th>
                  <th className="py-2 px-2 text-right">Resp.</th>
                  <th className="py-2 pl-2">Primer envío</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {p.campanas.map((c) => (
                  <tr key={c.id} className="align-top">
                    <td className="py-2 pr-3 min-w-[180px]">
                      <Link to={`${R_EMAILS}/campanas/${encodeURIComponent(String(c.id))}`} className="font-semibold text-violet-700 hover:underline break-words">{c.titulo || `Campaña ${c.id}`}</Link>
                    </td>
                    <td className="py-2 px-2 whitespace-nowrap"><ChipEstadoPersona estado={c.estado} /></td>
                    <td className="py-2 px-2 text-right tabular-nums">{num(c.enviados)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{c.aperturas === null ? <span className="text-slate-300" title="No se mide en esta campaña">—</span> : num(c.aperturas)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{c.clics === null ? <span className="text-slate-300" title="No se mide en esta campaña">—</span> : num(c.clics)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{num(c.respuestas)}</td>
                    <td className="py-2 pl-2 whitespace-nowrap text-slate-600">{diaMesHora(c.primerEnvio)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-[11px] text-slate-400">«—»: esa campaña no mide aperturas o clics (está apagado en Snov).</p>
          </div>
        )}
      </Tarjeta>

      <div className="grid gap-6 lg:grid-cols-2">
        <Tarjeta titulo="Lo que contestó">
          {!p.respuestas.length ? <Vacio>No contestó ningún mail.</Vacio> : (
            <ul className="divide-y divide-slate-100">
              {p.respuestas.map((x) => (
                <li key={x.id} className="py-3 first:pt-0 last:pb-0">
                  <p className="text-xs text-slate-400">
                    {diaMesHora(x.recibidaEn)} · {x.campana.titulo || `Campaña ${x.campana.id}`}
                    {x.pideBaja && <span className="ml-2 align-middle text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-700">Pide la baja</span>}
                  </p>
                  <p className="mt-1 text-sm text-slate-800 whitespace-pre-wrap break-words">{x.texto || <span className="text-slate-400">(sin texto)</span>}</p>
                </li>
              ))}
            </ul>
          )}
        </Tarjeta>
        <Tarjeta titulo="Lo que le pasó" sub="Del más nuevo al más viejo (hasta 200)">
          {!p.eventos.length ? <Vacio>Sin movimientos guardados.</Vacio> : (
            <ol className="space-y-2.5">
              {p.eventos.map((e, i) => {
                const t = TEXTO_EVENTO[e.tipo] || { texto: e.tipo, cls: "bg-slate-300" };
                return (
                  <li key={`${e.fecha}-${e.tipo}-${i}`} className="flex items-start gap-2.5 text-sm">
                    <span aria-hidden="true" className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${t.cls}`} />
                    <div className="min-w-0">
                      <p><span className="font-semibold text-slate-900">{t.texto}</span> <span className="text-xs text-slate-400">· {diaMesHora(e.fecha)}</span></p>
                      <p className="text-xs text-slate-500 break-words">{e.asunto ? asuntoLegible(e.asunto) : "Sin asunto"} · {e.campana || `Campaña ${e.campanaId}`}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </Tarjeta>
      </div>
    </div>
  );
}
